import { useEffect, useState } from 'react';
import { staffService } from 'services';
import {
  commonConstants,
  staffConstants,
  implementConstants,
} from 'constants/index';
import {
  IStaff,
  IStaffParams,
  IRegionalSaleAdmin,
  IGetRegionalSaleAdminParams,
  IPagination,
} from 'interfaces';
import { orderBy } from 'lodash';

const { STATUS_ASSIGNMENT_STATUS } = implementConstants;
const { DEFAULT_SIZE_CHANGER } = commonConstants;
const { DEFAULT_PARAMS_REGIONAL_ADMIN } = staffConstants;

const useStaff = (defaultParam: IStaffParams) => {
  const [staffs, setStaffs] = useState<IStaff[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IStaffParams>(defaultParam);
  const [pagination, setPagination] = useState<IPagination>({
    ...DEFAULT_SIZE_CHANGER,
  });

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const { data } = await staffService.getStaff(params);
      const { staffs, pagination: pager } = data;
      setStaffs(staffs);

      setPagination({
        ...pagination,
        total: pager.totalItems,
        current: pager.currentPage,
        pageSize: pager.currentPage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination: IPagination) => {
    const newQueryParams = {
      ...params,
      page: Number(pagination.current),
      pageSize: Number(pagination.pageSize),
    };
    setParams(newQueryParams);
  };

  useEffect(() => {
    fetchStaff().then();
  }, [params]);

  return {
    staffs,
    loading,
    params,
    setParams,
    pagination,
    handleTableChange,
  };
};

const useRegionalSale = () => {
  const [params, setParams] = useState<IGetRegionalSaleAdminParams>(
    DEFAULT_PARAMS_REGIONAL_ADMIN
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [regionalAdmins, setRegionalAdmins] = useState<IRegionalSaleAdmin[]>(
    []
  );
  const [resultSearchAdmin, setResultSearchAdmin] = useState<
    IRegionalSaleAdmin[]
  >([]);

  const getRegionalSale = async () => {
    try {
      setLoading(true);
      const res = await staffService.getRegionalSaleAdmin();
      const sortData = orderBy(res.data, ['provinceName'], ['asc']);
      setRegionalAdmins(sortData);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (values: IGetRegionalSaleAdminParams) => {
    const newRegionalAdmins = regionalAdmins.filter(regional => {
      let filter = true;
      if (values.provinceCode) {
        filter = regional.provinceCode === values.provinceCode;
        if (!filter) return false;
      }
      if (values.assignedType === STATUS_ASSIGNMENT_STATUS.UNASSIGNED.value) {
        filter = regional.staffEmail === null;
      } else if (
        values.assignedType === STATUS_ASSIGNMENT_STATUS.ASSIGNED.value
      ) {
        filter = regional.staffEmail !== null;
      }
      return filter;
    });
    setParams(values);
    setResultSearchAdmin(newRegionalAdmins);
  };

  const onAssignAdmin = async (provinceCode: string, staffId?: number) => {
    const payload = { provinceCode, staffId };
    await staffService.assignSaleAdmin(payload);
    await getRegionalSale();
  };

  useEffect(() => {
    getRegionalSale();
  }, []);

  useEffect(() => {
    onSearch(params);
  }, [regionalAdmins]);

  return {
    getRegionalSale,
    resultSearchAdmin,
    loading,
    onSearch,
    onAssignAdmin,
    params,
  };
};
export default {
  useStaff,
  useRegionalSale,
};
